import {
  participantCloudLayout,
  participantMotion,
  participantRowOffsets,
} from "./media/participant-cloud.ts";

export type ParticipantCloudView = {
  id: string;
  name: string;
  picture: string;
  self: boolean;
  screen: boolean;
  camera: boolean;
  connecting: boolean;
  microphoneOff: boolean;
  notListening: boolean;
  canOpenScreen: boolean;
};

export function participantCloudPositions(people: ParticipantCloudView[]) {
  const positions = participantCloudLayout(people.length);
  const offsets = participantRowOffsets(people, 128, 190, 64);
  return people.map((person, index) => ({
    person,
    ...positions[index],
    offset: offsets.get(person.id)!,
    motion: participantMotion(person.id),
  }));
}
export type PositionedParticipant = ReturnType<typeof participantCloudPositions>[number];
export type CloudEntry = PositionedParticipant & { leaving: boolean };
export const participantCloudKey = (person: ParticipantCloudView) =>
  person.self ? "self" : person.id;

// Keep an exiting participant's last position and media ref until its animation
// ends. A return before then reuses the same key and cancels the exit.
export function reconcileCloudEntries(
  previous: CloudEntry[],
  people: ParticipantCloudView[],
): CloudEntry[] {
  const active = participantCloudPositions(people).map((entry) => ({ ...entry, leaving: false }));
  const byKey = new Map(active.map((entry) => [participantCloudKey(entry.person), entry]));
  // Position by the sorted roster, but preserve insertion/paint order as the
  // legacy cloud did. This matters when large orbs overlap on narrow screens.
  const retained = previous.map((entry) => {
    const key = participantCloudKey(entry.person);
    const next = byKey.get(key);
    byKey.delete(key);
    return next || (entry.leaving ? entry : { ...entry, leaving: true });
  });
  return [...retained, ...byKey.values()];
}

export function participantCloudSpacing(
  people: ParticipantCloudView[],
  width: number,
  height: number,
): number {
  const positions = participantCloudLayout(people.length);
  const size = people.some((person) => person.camera) ? 190 : 128;
  const spanX =
    Math.max(...positions.map((p) => p.x), 0) - Math.min(...positions.map((p) => p.x), 0);
  const spanY =
    Math.max(...positions.map((p) => p.y), 0) - Math.min(...positions.map((p) => p.y), 0);
  return Math.max(
    64,
    Math.min(
      size === 190 ? 260 : 200,
      spanX ? (width - size - 24) / spanX : 160,
      spanY ? (height - size - 24) / spanY : 160,
    ),
  );
}

// Streams stay controller-owned. A cloud camera is a second view of the tile's
// stream, never another LiveKit attachment or a value in the React store.
export class ParticipantCloudMedia {
  private videos = new Map<string, HTMLVideoElement>();
  private source: (_id: string) => MediaStream | null;
  constructor(source: (_id: string) => MediaStream | null) {
    this.source = source;
  }
  video(id: string) {
    return this.videos.get(id) || null;
  }
  register(id: string, video: HTMLVideoElement | null) {
    const previous = this.videos.get(id);
    if (previous === video) return;
    if (previous) previous.srcObject = null;
    if (!video) this.videos.delete(id);
    else {
      this.videos.set(id, video);
      this.sync(id);
    }
  }
  sync(id: string) {
    const video = this.videos.get(id);
    if (!video) return;
    const stream = this.source(id);
    if (video.srcObject !== stream) {
      video.srcObject = stream;
      if (stream) void video.play().catch(() => {});
    }
    video.hidden = !stream;
  }
  dispose() {
    for (const video of this.videos.values()) video.srcObject = null;
    this.videos.clear();
  }
}
